import * as THREE from "three";

/**
 * A pointable spatial surface.
 *
 * One mechanism serves every menu in the app: draw to a canvas, declare the
 * rectangles that are interactive, and translate a controller ray into canvas
 * coordinates to find what is under the pointer. That keeps the visual language
 * in one place — so panels actually look like each other — and means adding a
 * control is declaring a row, not writing new hit-testing.
 *
 * Styling follows Horizon OS: dark translucent glass, generous corner radius,
 * restrained cyan accent, large targets. Panels are world-locked and placed at
 * Meta's published reading distances rather than pinned to the face, because
 * head-locked content swims under timewarp reprojection.
 *
 * Orientation note: this deliberately uploads through an ImageBitmap with
 * `imageOrientation: "flipY"`. Spark issues raw GL texture uploads that leave
 * three's cached UNPACK_FLIP_Y_WEBGL state stale, so a plain canvas texture
 * comes out upright or inverted depending on load order. Baking the flip into
 * the bitmap removes that dependency entirely.
 */

const CANVAS_W = 720;
const ROW_H = 74;
const HEADER_H = 104;
const PAD = 28;
/** Metres of panel width per canvas pixel — sets the apparent size. */
const METRES_PER_PX = 0.00072;

export interface UIRow {
  readonly id: string;
  readonly label: string;
  /** Right-aligned secondary text. */
  readonly value?: string;
  readonly selected?: boolean;
  readonly disabled?: boolean;
}

export interface UIModel {
  readonly title: string;
  readonly eyebrow: string;
  readonly rows: readonly UIRow[];
  readonly footer?: string;
}

interface HitRegion {
  readonly id: string;
  readonly top: number;
  readonly bottom: number;
  readonly disabled: boolean;
}

export class UISurface {
  readonly group = new THREE.Group();
  readonly #canvas = document.createElement("canvas");
  readonly #ctx: CanvasRenderingContext2D;
  readonly #texture: THREE.Texture;
  readonly #mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  readonly #regions: HitRegion[] = [];
  readonly #localPoint = new THREE.Vector3();
  readonly #plane = new THREE.Plane();
  readonly #hitPoint = new THREE.Vector3();

  #model: UIModel = { title: "", eyebrow: "", rows: [] };
  #hovered: string | null = null;
  #dirty = true;
  #uploading = false;
  #widthM = 0;
  #heightM = 0;

  constructor() {
    const ctx = this.#canvas.getContext("2d");
    if (ctx === null) {
      throw new Error("2D context unavailable");
    }
    this.#ctx = ctx;
    this.#texture = new THREE.Texture(this.#canvas);
    this.#texture.colorSpace = THREE.SRGBColorSpace;
    this.#texture.anisotropy = 4;
    this.#texture.flipY = false;

    this.#mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        map: this.#texture,
        transparent: true,
        depthWrite: false
      })
    );
    this.#mesh.renderOrder = 1000;
    this.group.add(this.#mesh);
  }

  get visible(): boolean {
    return this.group.visible;
  }

  set visible(value: boolean) {
    this.group.visible = value;
  }

  setModel(model: UIModel): void {
    this.#model = model;
    this.#dirty = true;
  }

  /**
   * Casts a ray at the surface and returns the row under it.
   * Also records the hover so the next repaint highlights it.
   */
  pick(origin: THREE.Vector3, direction: THREE.Vector3): string | null {
    if (!this.group.visible || this.#heightM === 0) {
      return null;
    }
    this.group.updateWorldMatrix(true, false);
    const normal = new THREE.Vector3(0, 0, 1).applyQuaternion(
      this.group.getWorldQuaternion(new THREE.Quaternion())
    );
    const centre = this.group.getWorldPosition(new THREE.Vector3());
    this.#plane.setFromNormalAndCoplanarPoint(normal, centre);

    const denominator = normal.dot(direction);
    if (Math.abs(denominator) < 1e-6) {
      return null;
    }
    const distance = this.#plane.distanceToPoint(origin) / -denominator;
    if (distance < 0 || distance > 12) {
      return null;
    }
    this.#hitPoint.copy(origin).addScaledVector(direction, distance);

    // Into the surface's own frame, then into canvas pixels.
    this.#localPoint.copy(this.#hitPoint);
    this.group.worldToLocal(this.#localPoint);
    const u = this.#localPoint.x / this.#widthM + 0.5;
    const v = 0.5 - this.#localPoint.y / this.#heightM;
    if (u < 0 || u > 1 || v < 0 || v > 1) {
      this.#setHover(null);
      return null;
    }

    const py = v * (this.#heightM / METRES_PER_PX);
    const hit = this.#regions.find(
      (region) => py >= region.top && py < region.bottom && !region.disabled
    );
    this.#setHover(hit?.id ?? null);
    return hit?.id ?? null;
  }

  clearHover(): void {
    this.#setHover(null);
  }

  #setHover(id: string | null): void {
    if (this.#hovered !== id) {
      this.#hovered = id;
      this.#dirty = true;
    }
  }

  update(): void {
    if (!this.#dirty) {
      return;
    }
    this.#dirty = false;
    this.#paint();
    void this.#upload();
  }

  #paint(): void {
    const rows = this.#model.rows;
    const height = HEADER_H + rows.length * ROW_H + (this.#model.footer ? 56 : 0) + PAD;
    this.#canvas.width = CANVAS_W;
    this.#canvas.height = height;

    this.#widthM = CANVAS_W * METRES_PER_PX;
    this.#heightM = height * METRES_PER_PX;
    this.#mesh.scale.set(this.#widthM, this.#heightM, 1);

    const ctx = this.#ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, CANVAS_W, height);

    const bg = ctx.createLinearGradient(0, 0, 0, height);
    bg.addColorStop(0, "rgba(17, 26, 37, 0.94)");
    bg.addColorStop(1, "rgba(9, 14, 21, 0.96)");
    ctx.fillStyle = bg;
    ctx.beginPath();
    ctx.roundRect(4, 4, CANVAS_W - 8, height - 8, 30);
    ctx.fill();
    ctx.strokeStyle = "rgba(140, 200, 225, 0.24)";
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.fillStyle = "#6ee7ff";
    ctx.font = "600 20px ui-monospace, Menlo, monospace";
    ctx.letterSpacing = "3px";
    ctx.fillText(this.#model.eyebrow.toUpperCase(), PAD + 8, 44);
    ctx.letterSpacing = "0px";

    ctx.fillStyle = "#f2fbff";
    ctx.font = "600 34px system-ui, -apple-system, sans-serif";
    ctx.fillText(this.#model.title, PAD + 8, 86);

    this.#regions.length = 0;
    let y = HEADER_H;
    for (const row of rows) {
      const hovered = this.#hovered === row.id && !row.disabled;
      this.#regions.push({
        id: row.id,
        top: y,
        bottom: y + ROW_H,
        disabled: row.disabled ?? false
      });

      if (hovered || row.selected) {
        ctx.fillStyle = hovered
          ? "rgba(110, 231, 255, 0.20)"
          : "rgba(139, 123, 255, 0.16)";
        ctx.beginPath();
        ctx.roundRect(PAD, y + 5, CANVAS_W - PAD * 2, ROW_H - 10, 16);
        ctx.fill();
      }
      if (row.selected) {
        ctx.fillStyle = "#8b7bff";
        ctx.beginPath();
        ctx.roundRect(PAD + 4, y + 18, 5, ROW_H - 36, 3);
        ctx.fill();
      }

      ctx.fillStyle = row.disabled
        ? "rgba(150, 175, 190, 0.45)"
        : hovered
          ? "#ffffff"
          : "#e2f2fb";
      ctx.font = "500 27px system-ui, -apple-system, sans-serif";
      const label =
        row.label.length > 30 ? `${row.label.slice(0, 29)}…` : row.label;
      ctx.fillText(label, PAD + 24, y + ROW_H / 2 + 10);

      if (row.value !== undefined) {
        ctx.fillStyle = "rgba(176, 205, 222, 0.9)";
        ctx.font = "400 23px ui-monospace, Menlo, monospace";
        ctx.textAlign = "right";
        ctx.fillText(row.value, CANVAS_W - PAD - 20, y + ROW_H / 2 + 9);
        ctx.textAlign = "left";
      }
      y += ROW_H;
    }

    if (this.#model.footer !== undefined) {
      ctx.fillStyle = "rgba(150, 180, 198, 0.7)";
      ctx.font = "400 21px system-ui, -apple-system, sans-serif";
      ctx.fillText(this.#model.footer, PAD + 8, y + 34);
    }

    this.#texture.needsUpdate = true;
  }

  async #upload(): Promise<void> {
    if (this.#uploading) {
      return;
    }
    this.#uploading = true;
    try {
      const bitmap = await createImageBitmap(this.#canvas, {
        imageOrientation: "flipY"
      });
      this.#texture.image = bitmap;
      this.#texture.needsUpdate = true;
    } catch {
      this.#texture.image = this.#canvas;
      this.#texture.needsUpdate = true;
    } finally {
      this.#uploading = false;
    }
  }
}

/**
 * Places a set of surfaces in a gentle arc in front of the viewer.
 *
 * Summoned rather than persistent: menus appear where you are looking when you
 * ask for them and stay put in the world, so they never sit between you and the
 * thing you are making.
 */
export function placeArc(
  surfaces: readonly UISurface[],
  headPosition: THREE.Vector3,
  headYaw: number,
  distance = 0.9
): void {
  const spread = THREE.MathUtils.degToRad(26);
  const middle = (surfaces.length - 1) / 2;
  surfaces.forEach((surface, index) => {
    const yaw = headYaw + (index - middle) * spread;
    const sin = Math.sin(yaw);
    const cos = Math.cos(yaw);
    surface.group.position.set(
      headPosition.x + sin * distance,
      headPosition.y - 0.22,
      headPosition.z + cos * distance
    );
    surface.group.quaternion.setFromAxisAngle(
      new THREE.Vector3(0, 1, 0),
      Math.atan2(-sin, -cos)
    );
  });
}
